//Global imports
import { CRow, CButton, CInputGroup, CFormInput } from '@coreui/react';
import React, { useEffect, useState } from 'react';
import { cilFolder } from '@coreui/icons';
import CIcon from '@coreui/icons-react';

//Component imports
import DocumentCategoryCard from '../DocumentCategoryCard';
import Loader from '../../../../components/Layout/Loader';

//File imports
import Logger from '../../../../services/Logger/Logger';
import { Language } from '../../../../language/English';
import { DocumentCategoryService } from '../../Api/DocumentCategoryService';
import { DocumentCategoryPojo } from '../../Api/DocumentCategoryPojo';
import {
    DOCUMENT_CATEGORY_PAGE_STATUS_INACTIVE,
    DOCUMENT_CATEGORY_PAGE_TEMPLATE_STATUS_DELETED,
} from '../../Api/DocumentCategoryConstants';
import useInitialLoad from '../../../../services/Hooks/useInitialLoad';
import CreateDocumentCategoryModal from '../CreateDocumentCategoryModal';
import Notification from '../../../../services/Notification/Notification';
import StringUtils from '../../../../services/StringUtils/StringUtils';

interface DocumentTemplateListProps {
    stringUtils: StringUtils;
    notification: Notification;
    logger: Logger;
    documentCategoryService: DocumentCategoryService;
    navigate: any;
}

const DocumentCategoryList: React.FC<DocumentTemplateListProps> = ({
    stringUtils,
    notification,
    logger,
    documentCategoryService,
    navigate,
}) => {
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [documentCategories, setDocumentCategories] = useState<
        DocumentCategoryPojo[][] | null
    >(null);
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [editingCategory, setEditingCategory] =
        useState<DocumentCategoryPojo | null>(null);
    const [searchText, setSearchText] = useState<string>('');

    useInitialLoad({
        loaderCallback: () => {
            setShowLoader(true);
            fetchAllDocumentCategories();
        },
    });

    const handleEdit = (category: DocumentCategoryPojo) => {
        setEditingCategory(category);
        setShowCreateModal(true);
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setEditingCategory(null);
    };

    const handleSuccess = () => {
        fetchAllDocumentCategories();
        handleModalClose();
    };

    const fetchAllDocumentCategories = async () => {
        try {
            const response = await documentCategoryService.getAll();
            if (response && response.isSuccess) {
                const chunkedCategories: any = [];
                for (
                    let i = 0;
                    i < response?.documentCategories.length;
                    i += 2
                ) {
                    chunkedCategories.push(
                        response?.documentCategories.slice(i, i + 2),
                    );
                }
                setDocumentCategories(chunkedCategories);
            } else {
                notification.error(
                    Language.CANNOT_LOAD_DOCUMENT_GROUP_PAGES_ERROR,
                );
            }
            setShowLoader(false);
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            setShowLoader(false);
            logger.error(e);
        }
    };

    const changeDocumentCategoryStatus = async (
        documentCategoryPageId: number,
        status: number,
        documentCategoryPageName: string,
    ) => {
        setShowLoader(true);
        try {
            let response = await documentCategoryService.delete(
                documentCategoryPageId,
                status,
            );
            if (response) {
                if (status === DOCUMENT_CATEGORY_PAGE_TEMPLATE_STATUS_DELETED) {
                    notification.success(
                        documentCategoryPageName +
                            ' ' +
                            Language.DELETED_SUCCESSFULLY,
                    );
                } else {
                    let msg =
                        status === DOCUMENT_CATEGORY_PAGE_STATUS_INACTIVE
                            ? Language.INACTIVATED_SUCCESSFULLY
                            : Language.ACTIVATED_SUCCESSFULLY;
                    notification.success(documentCategoryPageName + ' ' + msg);
                }
                fetchAllDocumentCategories();
            } else {
                notification.error(
                    Language.CANNOT_CHANGE_DOCUMENT_GROUP_PAGE_STATUS_ERROR,
                );
                setShowLoader(false);
            }
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            setShowLoader(false);
            logger.error(e);
        }
    };

    const filteredCategories = documentCategories
        ?.map((row: DocumentCategoryPojo[]) =>
            row.filter((category: DocumentCategoryPojo) =>
                category.name.toLowerCase().includes(searchText.toLowerCase()),
            ),
        )
        .filter((row) => row.length > 0);

    return (
        <>
            {showLoader ? <Loader /> : null}

            <div className="d-flex justify-content-between align-items-center mb-4 search-box-container">
                {documentCategories && documentCategories?.length > 0 ? (
                    <CInputGroup className="w-50 search-box">
                        <CFormInput
                            placeholder={Language.SEARCH_DOCUMENT_GROUP}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </CInputGroup>
                ) : (
                    <div></div>
                )}

                <CButton
                    color="primary"
                    onClick={() => setShowCreateModal(true)}
                >
                    {Language.CREATE_DOCUMENT_GROUP_PAGE}
                </CButton>
            </div>

            {filteredCategories && filteredCategories.length > 0 ? (
                <CRow>
                    {filteredCategories.map((row: any, rowIndex) =>
                        row.map((category: DocumentCategoryPojo) => (
                            <DocumentCategoryCard
                                key={category.documentCategoryPageId}
                                navigate={navigate}
                                documentCategory={category}
                                deleteDocumentCategory={() =>
                                    changeDocumentCategoryStatus(
                                        category.documentCategoryPageId,
                                        DOCUMENT_CATEGORY_PAGE_TEMPLATE_STATUS_DELETED,
                                        category.name,
                                    )
                                }
                                changeDocumentCategoryStatus={(
                                    status: number,
                                ) =>
                                    changeDocumentCategoryStatus(
                                        category.documentCategoryPageId,
                                        status,
                                        category.name,
                                    )
                                }
                                onEdit={handleEdit}
                            />
                        )),
                    )}
                </CRow>
            ) : (documentCategories &&
                  Array.isArray(documentCategories) &&
                  documentCategories.length === 0) ||
              filteredCategories?.length === 0 ? (
                <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{ minHeight: '60vh' }}
                >
                    <CIcon
                        icon={cilFolder}
                        size="3xl"
                        className="mb-3 text-secondary"
                    />
                    <h4 className="mb-3 text-center text-secondary fs-6">
                        {Language.NO_DOCUMENT_GROUP_PAGES} <br />
                        {Language.CREATE_HERE}
                    </h4>
                    <CButton
                        color="primary"
                        onClick={() => setShowCreateModal(true)}
                    >
                        {Language.CREATE_DOCUMENT_GROUP_PAGE}
                    </CButton>
                </div>
            ) : null}

            {showCreateModal && (
                <CreateDocumentCategoryModal
                    stringUtils={stringUtils}
                    notification={notification}
                    documentCategoryService={documentCategoryService}
                    logger={logger}
                    visible={showCreateModal}
                    onClose={handleModalClose}
                    onSuccess={handleSuccess}
                    mode={editingCategory ? 'edit' : 'create'}
                    categoryData={editingCategory}
                />
            )}
        </>
    );
};

export default DocumentCategoryList;
